import React, { useState, useEffect } from 'react';
import { Cloud, ThermometerSun, Mountain, Sun, CloudRain, CloudDrizzle, CloudSnow, Wind, CloudFog } from 'lucide-react';

const WeatherWidget = () => {
  const [weather, setWeather] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    fetchWeather();
    // Refresh weather every 10 minutes
    const interval = setInterval(fetchWeather, 600000);
    return () => clearInterval(interval);
  }, []);

  // Get weather icon based on condition
  const getWeatherIcon = (condition) => {
    if (!condition) return <Sun className="w-6 h-6 text-yellow-500" />;
    
    const conditionLower = condition.toLowerCase();
    
    if (conditionLower.includes('rain') || conditionLower.includes('shower')) {
      return <CloudRain className="w-6 h-6 text-blue-500" />;
    } else if (conditionLower.includes('drizzle')) {
      return <CloudDrizzle className="w-6 h-6 text-blue-400" />;
    } else if (conditionLower.includes('snow')) {
      return <CloudSnow className="w-6 h-6 text-blue-300" />;
    } else if (conditionLower.includes('cloud') || conditionLower.includes('overcast')) {
      return <Cloud className="w-6 h-6 text-gray-500" />;
    } else if (conditionLower.includes('fog') || conditionLower.includes('mist')) {
      return <CloudFog className="w-6 h-6 text-gray-400" />;
    } else if (conditionLower.includes('wind')) {
      return <Wind className="w-6 h-6 text-blue-400" />;
    } else if (conditionLower.includes('clear') || conditionLower.includes('sunny') || conditionLower.includes('fine')) {
      return <Sun className="w-6 h-6 text-yellow-500" />;
    }
    
    // Default to sun icon
    return <Sun className="w-6 h-6 text-yellow-500" />;
  };

  const fetchWeather = async () => {
    try {
      setLoading(true);
      setError(false);
      
      // Fetch weather data from backend proxy (avoids CORS issues)
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/weather/coober-pedy`);
      
      if (!response.ok) {
        throw new Error('Failed to fetch weather data');
      }
      
      const data = await response.json();
      
      // Set weather data
      setWeather({
        temp: data.temp,
        apparentTemp: data.apparent_temp,
        condition: data.condition || 'Clear',
        humidity: data.humidity,
        windSpeed: data.wind_speed,
        windDir: data.wind_dir,
        observationTime: data.observation_time,
        maxTemp: data.max_temp,
        minTemp: data.min_temp
      });
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching weather:', err);
      setError(true);
      setLoading(false);
      // Set fallback data
      setWeather({
        temp: 25,
        condition: 'Sunny',
        humidity: 30,
        windSpeed: 10
      });
    }
  };

  if (loading && !weather) {
    return (
      <section className="py-8 bg-gradient-to-r from-blue-50 to-cyan-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-center items-center gap-8">
            <div className="animate-pulse h-32 w-64 bg-white/50 rounded-2xl"></div>
            <div className="animate-pulse h-32 w-64 bg-white/50 rounded-2xl"></div>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="py-12 bg-gradient-to-r from-blue-50 to-cyan-50 border-y border-blue-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row justify-center items-center gap-8">
          
          {/* Coober Pedy Current Weather */}
          <div className="bg-white rounded-3xl shadow-2xl p-8 w-full md:w-96 border-2 border-blue-200 hover:shadow-3xl transition-all duration-300">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center gap-3">
                {getWeatherIcon(weather?.condition)}
                <span className="text-base font-bold text-gray-600">Current Weather</span>
              </div>
              {error && (
                <span className="text-xs text-orange-500 ml-2">Est.</span>
              )}
            </div>
            
            <div className="space-y-4 text-center">
              <div className="space-y-1">
                <h3 className="text-4xl font-black text-transparent bg-clip-text bg-gradient-to-r from-orange-400 via-yellow-300 to-orange-500">
                  COOBER PEDY
                </h3>
                <p className="text-lg text-gray-600 font-medium mt-2">{weather?.condition || 'Clear'}</p>
              </div>
              
              <div className="flex items-baseline justify-center gap-2 py-4">
                <div className="text-7xl font-black text-blue-600">
                  {weather?.temp ? Math.round(weather.temp) : '25'}
                </div>
                <div className="text-4xl font-bold text-gray-400">°C</div>
              </div>
              
              <div>
                {weather?.apparentTemp && (
                  <p className="text-base text-gray-600 font-medium">
                    Feels like {Math.round(weather.apparentTemp)}°C
                  </p>
                )}
                {weather?.observationTime && (
                  <p className="text-sm text-gray-400 mt-1">
                    Measured at {weather.observationTime}
                  </p>
                )}
              </div>
              
              <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-200">
                {weather?.humidity && (
                  <div className="text-center">
                    <span className="text-sm text-gray-500 block mb-1">Humidity</span>
                    <p className="text-lg font-bold text-gray-800">{weather.humidity}%</p>
                  </div>
                )}
                {weather?.windSpeed !== undefined && (
                  <div className="text-center">
                    <span className="text-sm text-gray-500 block mb-1">Wind</span>
                    <p className="text-lg font-bold text-gray-800">
                      {weather.windSpeed} km/h {weather?.windDir || ''}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Underground Mine Temperature - Static */}
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-3xl shadow-2xl p-8 w-full md:w-96 border-2 border-orange-400 hover:shadow-3xl transition-all duration-300 text-white">
            <div className="flex items-center justify-center mb-6">
              <div className="flex items-center gap-3">
                <Mountain className="w-7 h-7 text-orange-100" />
                <span className="text-base font-bold text-orange-100">Underground</span>
              </div>
              <ThermometerSun className="w-6 h-6 text-orange-200 ml-3" />
            </div>
            
            <div className="space-y-4 text-center">
              <div>
                <h3 className="text-2xl font-bold mb-2">Inside Umoona Opal Mine</h3>
                <p className="text-base text-orange-100 font-medium">Year-round constant</p>
              </div>
              
              <div className="flex items-baseline justify-center gap-2 py-4">
                <div className="text-7xl font-black">
                  23.3
                </div>
                <div className="text-4xl font-bold text-orange-100">°C</div>
              </div>
              
              <p className="text-base text-orange-100 leading-relaxed pt-4 border-t border-orange-400">
                Perfect climate underground - naturally cool and comfortable all year
              </p>
            </div>
          </div>

        </div>
        
        {/* Small disclaimer */}
        {!error && weather?.observationTime && (
          <p className="text-center text-xs text-gray-500 mt-4">
            Weather data from Bureau of Meteorology • Updated regularly
          </p>
        )}
      </div>
    </section>
  );
};

export default WeatherWidget;
